import React, {useEffect, useState} from 'react';
import {
    Box,
    Dialog,
    DialogTitle,
    FormControl,
    Grid,
    MenuItem,
    Select,
    TextField,
    Typography,
    OutlinedInput
} from '@mui/material';
import {FieldGroup, FieldLabel, StyledDialogActions, StyledDialogContent} from './CronConfigDialog.styled';
import {ActionButton, CancelButton} from '../common/ActionButtons';
import {useTranslation} from 'react-i18next';
import FieldHelpSuffix from '../common/FieldHelpSuffix';

interface CronConfigDialogProps {
    open: boolean;
    currentCron: string;
    onClose: () => void;
    onSave: (newCron: string) => void;
    mode?: 'edit' | 'create';
}

const CronConfigDialog: React.FC<CronConfigDialogProps> = ({open, currentCron, onClose, onSave, mode = 'edit'}) => {
    const {t} = useTranslation();
    const [seconds, setSeconds] = useState<string>('0');
    const [minutes, setMinutes] = useState<string>('0');
    const [hours, setHours] = useState<string>('0');
    const [dayOfMonth, setDayOfMonth] = useState<string>('*');
    const [month, setMonth] = useState<string>('*');
    const [dayOfWeek, setDayOfWeek] = useState<string>('?');

    useEffect(() => {
        if (open && currentCron) {
            const parts = currentCron.split(' ');
            if (parts.length === 6) {
                setSeconds(parts[0]);
                setMinutes(parts[1]);
                setHours(parts[2]);
                setDayOfMonth(parts[3]);
                setMonth(parts[4]);
                setDayOfWeek(parts[5]);
            } else if (parts.length === 5) {
                setSeconds('0');
                setMinutes(parts[0]);
                setHours(parts[1]);
                setDayOfMonth(parts[2]);
                setMonth(parts[3]);
                setDayOfWeek(parts[4] === '*' && parts[2] !== '*' ? '?' : parts[4]);
            }
        } else if (open) {
            setSeconds('0');
            setMinutes('0');
            setHours('12');
            setDayOfMonth('*');
            setMonth('*');
            setDayOfWeek('?');
        }
    }, [open, currentCron]);

    const handleSave = () => {
        let finalDayOfMonth = dayOfMonth;
        let finalDayOfWeek = dayOfWeek;

        if (finalDayOfMonth !== '*' && finalDayOfMonth !== '?') {
            finalDayOfWeek = '?';
        } else if (finalDayOfWeek !== '*' && finalDayOfWeek !== '?') {
            finalDayOfMonth = '?';
        } else if (finalDayOfMonth === '?' && finalDayOfWeek === '?') {
            finalDayOfMonth = '*';
            finalDayOfWeek = '?';
        }

        const newCron = `${seconds} ${minutes} ${hours} ${finalDayOfMonth} ${month} ${finalDayOfWeek}`;
        onSave(newCron);
        onClose();
    };

    const constructedCron = `${seconds} ${minutes} ${hours} ${dayOfMonth === '*' && dayOfWeek !== '*' && dayOfWeek !== '?' ? '?' : dayOfMonth} ${month} ${dayOfMonth !== '*' && dayOfMonth !== '?' ? '?' : dayOfWeek}`;


    return (
        <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
            <DialogTitle>{t('cronConfig.title')}</DialogTitle>
            <StyledDialogContent>
                <Grid container spacing={2}>
                    <Grid item xs={12} sm={4}>
                        <FieldGroup>
                            <FieldLabel>{t('cronConfig.second')}</FieldLabel>
                            <TextField
                                value={seconds}
                                onChange={(e) => setSeconds(e.target.value)}
                                fullWidth
                                variant="outlined"
                                InputProps={{
                                    endAdornment: (
                                        <FieldHelpSuffix
                                            asAdornment
                                            title={t('cronConfig.help.seconds')}
                                            ariaLabel={t('cronConfig.help.seconds') as string}
                                        />
                                    ),
                                }}
                            />
                        </FieldGroup>
                    </Grid>
                    <Grid item xs={12} sm={4}>
                        <FieldGroup>
                            <FieldLabel>{t('cronConfig.minute')}</FieldLabel>
                            <TextField
                                value={minutes}
                                onChange={(e) => setMinutes(e.target.value)}
                                fullWidth
                                variant="outlined"
                                InputProps={{
                                    endAdornment: (
                                        <FieldHelpSuffix
                                            asAdornment
                                            title={t('cronConfig.help.minutes')}
                                            ariaLabel={t('cronConfig.help.minutes') as string}
                                        />
                                    ),
                                }}
                            />
                        </FieldGroup>
                    </Grid>
                    <Grid item xs={12} sm={4}>
                        <FieldGroup>
                            <FieldLabel>{t('cronConfig.hour')}</FieldLabel>
                            <TextField
                                value={hours}
                                onChange={(e) => setHours(e.target.value)}
                                fullWidth
                                variant="outlined"
                                InputProps={{
                                    endAdornment: (
                                        <FieldHelpSuffix
                                            asAdornment
                                            title={t('cronConfig.help.hours')}
                                            ariaLabel={t('cronConfig.help.hours') as string}
                                        />
                                    ),
                                }}
                            />
                        </FieldGroup>
                    </Grid>
                    <Grid item xs={12} sm={4}>
                        <FieldGroup>
                            <FieldLabel>{t('cronConfig.dayOfMonth')}</FieldLabel>
                            <FormControl fullWidth variant="outlined">
                                <Select
                                    value={dayOfMonth}
                                    onChange={(e) => setDayOfMonth(e.target.value)}
                                    sx={{
                                        '& .MuiSelect-icon': { right: 40 },
                                    }}
                                    input={
                                        <OutlinedInput
                                            endAdornment={
                                                <FieldHelpSuffix
                                                    asAdornment
                                                    title={t('cronConfig.help.dayOfMonth')}
                                                    ariaLabel={t('cronConfig.help.dayOfMonth') as string}
                                                />
                                            }
                                            sx={{ pr: '72px' }}
                                        />
                                    }
                                >
                                    <MenuItem value="*">{t('cronConfig.every')}</MenuItem>
                                    <MenuItem value="?">{t('cronConfig.ignoredWhenDayOfWeek')}</MenuItem>
                                    {[...Array(31)].map((_, i) => (
                                        <MenuItem key={i + 1} value={(i + 1).toString()}>
                                            {i + 1}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </FieldGroup>
                    </Grid>
                    <Grid item xs={12} sm={4}>
                        <FieldGroup>
                            <FieldLabel>{t('cronConfig.month')}</FieldLabel>
                            <FormControl fullWidth variant="outlined">
                                <Select
                                    value={month}
                                    onChange={(e) => setMonth(e.target.value)}
                                    sx={{
                                        '& .MuiSelect-icon': { right: 40 },
                                    }}
                                    input={
                                        <OutlinedInput
                                            endAdornment={
                                                <FieldHelpSuffix
                                                    asAdornment
                                                    title={t('cronConfig.help.month')}
                                                    ariaLabel={t('cronConfig.help.month') as string}
                                                />
                                            }
                                            sx={{ pr: '72px' }}
                                        />
                                    }
                                >
                                    <MenuItem value="*">{t('cronConfig.every')}</MenuItem>
                                    {[...Array(12)].map((_, i) => (
                                        <MenuItem key={i + 1} value={(i + 1).toString()}>
                                            {i + 1}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </FieldGroup>
                    </Grid>
                    <Grid item xs={12} sm={4}>
                        <FieldGroup>
                            <FieldLabel>{t('cronConfig.dayOfWeek')}</FieldLabel>
                            <FormControl fullWidth variant="outlined">
                                <Select
                                    value={dayOfWeek}
                                    onChange={(e) => setDayOfWeek(e.target.value)}
                                    sx={{
                                        '& .MuiSelect-icon': { right: 40 },
                                    }}
                                    input={
                                        <OutlinedInput
                                            endAdornment={
                                                <FieldHelpSuffix
                                                    asAdornment
                                                    title={t('cronConfig.help.dayOfWeek')}
                                                    ariaLabel={t('cronConfig.help.dayOfWeek') as string}
                                                />
                                            }
                                            sx={{ pr: '72px' }}
                                        />
                                    }
                                >
                                    <MenuItem value="*">{t('cronConfig.every')}</MenuItem>
                                    <MenuItem value="?">{t('cronConfig.ignoredWhenDayOfMonth')}</MenuItem>
                                    <MenuItem value="1">{t('cronConfig.monday')}</MenuItem>
                                    <MenuItem value="2">{t('cronConfig.tuesday')}</MenuItem>
                                    <MenuItem value="3">{t('cronConfig.wednesday')}</MenuItem>
                                    <MenuItem value="4">{t('cronConfig.thursday')}</MenuItem>
                                    <MenuItem value="5">{t('cronConfig.friday')}</MenuItem>
                                    <MenuItem value="6">{t('cronConfig.saturday')}</MenuItem>
                                    <MenuItem value="0">{t('cronConfig.sunday')}</MenuItem>
                                </Select>
                            </FormControl>
                        </FieldGroup>
                    </Grid>
                </Grid>
                <Box mt={4} textAlign="center">
                    <Typography variant="subtitle1" component="p" gutterBottom>
                        {t('cronConfig.cronExpression')}
                    </Typography>
                    <Typography variant="h6" component="p" sx={{fontFamily: 'monospace', fontWeight: 'bold'}}>
                        {constructedCron}
                    </Typography>
                </Box>
            </StyledDialogContent>
            <StyledDialogActions>
                <CancelButton onClick={onClose}>{t('cronConfig.cancel')}</CancelButton>
                <ActionButton onClick={handleSave}>
                    {mode === 'edit' ? t('cronConfig.save') : t('cronConfig.confirm')}
                </ActionButton>
            </StyledDialogActions>
        </Dialog>
    );
};

export default CronConfigDialog;
